import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, FloppyDisk, CalendarBlank, Truck, Package } from "@phosphor-icons/react";
import { DateTimePicker } from "@/components/DateTimePicker";

/**
 * Modal to edit an existing appointment (RCN pase).
 * Sends PUT /appointments/{id} with only the changed fields.
 */
export default function EditAppointmentModal({ appt, onClose, onSaved }) {
  const [providers, setProviders] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    when: appt.appointment_datetime ? new Date(appt.appointment_datetime) : null,
    provider_id: appt.provider_id || "",
    unit_type_id: appt.unit_type_id || "",
    qty_units: appt.qty_units ?? 1,
    qty_pallets: appt.qty_pallets ?? 0,
    qty_pieces: appt.qty_pieces ?? 0,
    notes: appt.notes || "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [p, u] = await Promise.all([
          api.get("/catalogs/providers"),
          api.get("/catalogs/unit-types"),
        ]);
        setProviders(p.data);
        setUnitTypes(u.data);
      } catch (err) {
        console.debug("[EditApptModal] load catalogs failed", err);
      }
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.when || !form.provider_id || !form.unit_type_id) {
      toast.error("Fecha, proveedor y unidad son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const body = {
        appointment_datetime: form.when.toISOString(),
        provider_id: form.provider_id,
        unit_type_id: form.unit_type_id,
        qty_units: Number(form.qty_units) || 0,
        qty_pallets: Number(form.qty_pallets) || 0,
        qty_pieces: Number(form.qty_pieces) || 0,
        notes: form.notes,
      };
      const { data } = await api.put(`/appointments/${appt.id}`, body);
      toast.success(`Cita ${data.folio} actualizada`);
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="edit-appt-modal">
      <div className="bg-[#111111] border border-white/15 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-[#0055FF]/20 border border-[#0055FF]/40 flex items-center justify-center">
            <CalendarBlank size={16} className="text-[#0055FF]" weight="fill" />
          </div>
          <div className="flex-1">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-white/50">
              Editar Pase
            </div>
            <div className="font-mono-data font-bold text-lg text-[#0055FF]">{appt.folio}</div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-sm text-white/50 hover:bg-white/5 hover:text-white"
            data-testid="edit-appt-close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-5">
          <Field label="Fecha y hora">
            <DateTimePicker value={form.when} onChange={(d) => update("when", d)} testId="edit-appt-when" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={<><Truck size={12} className="inline mr-1" /> Proveedor</>}>
              <select
                data-testid="edit-appt-provider"
                value={form.provider_id}
                onChange={(e) => update("provider_id", e.target.value)}
                className="input-dark"
              >
                <option value="">Selecciona…</option>
                {providers.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
            </Field>
            <Field label={<><Package size={12} className="inline mr-1" /> Tipo de Unidad</>}>
              <select
                data-testid="edit-appt-unit"
                value={form.unit_type_id}
                onChange={(e) => update("unit_type_id", e.target.value)}
                className="input-dark"
              >
                <option value="">Selecciona…</option>
                {unitTypes.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Unidades">
              <input
                type="number" min="0"
                data-testid="edit-appt-units"
                value={form.qty_units}
                onChange={(e) => update("qty_units", e.target.value)}
                className="input-dark"
              />
            </Field>
            <Field label="Tarimas">
              <input
                type="number" min="0"
                data-testid="edit-appt-pallets"
                value={form.qty_pallets}
                onChange={(e) => update("qty_pallets", e.target.value)}
                className="input-dark"
              />
            </Field>
            <Field label="Piezas">
              <input
                type="number" min="0"
                data-testid="edit-appt-pieces"
                value={form.qty_pieces}
                onChange={(e) => update("qty_pieces", e.target.value)}
                className="input-dark"
              />
            </Field>
          </div>

          <Field label="Notas">
            <textarea
              rows={3}
              data-testid="edit-appt-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Observaciones internas, referencia del proveedor…"
              className="input-dark resize-y"
            />
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-sm border border-white/10 text-sm text-white/70 hover:bg-white/5"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-appt-save"
              className="flex items-center gap-2 px-5 py-2 rounded-sm bg-[#0055FF] text-white text-sm font-semibold hover:brightness-110 active:scale-[0.98] disabled:opacity-50 transition-transform"
            >
              <FloppyDisk size={15} weight="fill" /> {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input-dark {
          width: 100%;
          padding: 0.55rem 0.7rem;
          border-radius: 2px;
          background: #0A0A0A;
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          font-size: 0.85rem;
          outline: none;
        }
        .input-dark:focus { border-color: #0055FF; box-shadow: 0 0 0 1px #0055FF; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
